import { useCallback, useEffect, useState } from 'react';

import { DBSchema, openDB, type IDBPDatabase } from 'idb';

interface DB extends DBSchema {
  case: {
    key: string;
    value: {
      caseId: string;
      facts: { [key: string]: boolean };
    };
    indexes: {
      caseId: string;
    };
  };
}

const DATABASE_NAME = 'wexler-db';

let dbPromise: Promise<IDBPDatabase<DB>> | null = null;

const getDb = async (): Promise<IDBPDatabase<DB>> => {
  if (dbPromise) return dbPromise;

  dbPromise = openDB<DB>(DATABASE_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('case')) {
        const objectStore = db.createObjectStore('case', { keyPath: 'caseId' });
        objectStore.createIndex('caseId', 'caseId', { unique: true });
      }
    },
  });

  return await dbPromise;
};

export function useSelectedFactsDb() {
  const [db, setDb] = useState<IDBPDatabase<DB>>();

  useEffect(() => {
    getDb().then(setDb);
  }, []);

  const doesCaseExist = useCallback(
    async (subsetId: string) => {
      if (!db) return null;
      let caseRecord = await db.get('case', subsetId);
      return caseRecord !== undefined;
    },
    [db],
  );

  const selectFact = useCallback(
    async (subsetId: string, factId: string) => {
      if (!db) return null;

      let caseRecord = await db.get('case', subsetId);

      if (!caseRecord) {
        // Create new case record with this fact selected
        caseRecord = {
          caseId: subsetId,
          facts: { [factId]: true },
        };
      } else {
        caseRecord.facts[factId] = true;
      }

      await db.put('case', caseRecord);
    },
    [db],
  );

  const deselectFact = useCallback(
    async (subsetId: string, factId: string) => {
      if (!db) return;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return;

      delete caseRecord.facts[factId];

      await db.put('case', caseRecord);
    },
    [db],
  );

  const isFactSelected = useCallback(
    async (subsetId: string, factId: string): Promise<boolean> => {
      if (!db) return false;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return false;

      return caseRecord.facts[factId] === true;
    },
    [db],
  );

  const deselectAll = useCallback(
    async (subsetId: string) => {
      if (!db) return null;
      await db.put('case', { caseId: subsetId, facts: {} });
    },
    [db],
  );

  const selectMultiple = useCallback(
    async (subsetId: string, factIds: string[]) => {
      if (!db) return null;

      let caseRecord = await db.get('case', subsetId);

      if (!caseRecord) {
        // Create new case record with these facts selected
        const facts: { [key: string]: boolean } = {};

        factIds.forEach((id) => {
          facts[id] = true;
        });

        caseRecord = {
          caseId: subsetId,
          facts,
        };
      } else {
        // Add all fact IDs to the existing record
        factIds.forEach((id) => {
          caseRecord!.facts[id] = true;
        });
      }

      await db.put('case', caseRecord);
    },
    [db],
  );

  const deselectMultiple = useCallback(
    async (subsetId: string, factIds: string[]) => {
      if (!db) return null;

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return;

      factIds.forEach((id) => {
        delete caseRecord.facts[id];
      });

      await db.put('case', caseRecord);
    },
    [db],
  );

  const getAllSelectedIds = useCallback(
    async (subsetId: string): Promise<string[]> => {
      if (!db) return [];

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return [];

      return Object.keys(caseRecord.facts);
    },
    [db],
  );

  const getAllSelectedFacts = useCallback(
    async (subsetId: string): Promise<{ [key: string]: boolean }> => {
      if (!db) return {};

      const caseRecord = await db.get('case', subsetId);
      if (!caseRecord) return {};

      return caseRecord.facts;
    },
    [db],
  );

  return {
    db,
    doesCaseExist,
    selectFact,
    deselectFact,
    isFactSelected,
    getAllSelectedIds,
    getAllSelectedFacts,
    deselectAll,
    selectMultiple,
    deselectMultiple,
  };
}
